/**
 * NRO Custom Sort Dropdown
 * Replaces native <select> for sort-by on collection/search pages with a
 * fully-styled custom dropdown, bypassing OS-native rendering on Windows.
 *
 * Fires a synthetic `input` event on the hidden native select so that
 * Shopify's FacetFiltersForm still handles the AJAX page update.
 */
(function () {
  'use strict';

  var SELECTOR = '.facet-filters__sort, .mobile-facets__sort .select__select';

  function buildCustomSelect(nativeSelect) {
    if (nativeSelect.dataset.nroCustomized) return;
    nativeSelect.dataset.nroCustomized = '1';

    // Visually hide the native select but keep it in the DOM for form submission
    nativeSelect.style.cssText =
      'position:absolute;opacity:0;pointer-events:none;width:1px;height:1px;overflow:hidden;';

    var wrapper = document.createElement('div');
    wrapper.className = 'nro-select';

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'nro-select__btn';
    btn.setAttribute('aria-haspopup', 'listbox');
    btn.setAttribute('aria-expanded', 'false');

    var labelSpan = document.createElement('span');
    labelSpan.className = 'nro-select__label';

    // SVG caret
    var svgNS = 'http://www.w3.org/2000/svg';
    var caret = document.createElementNS(svgNS, 'svg');
    caret.setAttribute('class', 'nro-select__caret');
    caret.setAttribute('viewBox', '0 0 10 6');
    caret.setAttribute('aria-hidden', 'true');
    var path = document.createElementNS(svgNS, 'path');
    path.setAttribute('d', 'M1 1l4 4 4-4');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '1.5');
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    caret.appendChild(path);

    btn.appendChild(labelSpan);
    btn.appendChild(caret);

    var list = document.createElement('ul');
    list.className = 'nro-select__list';
    list.setAttribute('role', 'listbox');

    var options = Array.from(nativeSelect.querySelectorAll('option'));
    var currentLabel = '';

    options.forEach(function (opt) {
      var li = document.createElement('li');
      li.className = 'nro-select__item';
      li.setAttribute('role', 'option');
      li.setAttribute('tabindex', '-1');
      li.dataset.value = opt.value;
      li.textContent = opt.textContent.trim();
      if (opt.selected) {
        li.setAttribute('aria-selected', 'true');
        currentLabel = li.textContent;
      } else {
        li.setAttribute('aria-selected', 'false');
      }
      list.appendChild(li);
    });

    labelSpan.textContent = currentLabel || options[0].textContent.trim();

    wrapper.appendChild(btn);
    wrapper.appendChild(list);
    nativeSelect.parentNode.insertBefore(wrapper, nativeSelect.nextSibling);

    // ── Open / Close ────────────────────────────────────────────────────────

    function openDropdown() {
      // Close any other open NRO dropdowns on the page
      document.querySelectorAll('.nro-select__list.is-open').forEach(function (el) {
        if (el !== list) {
          el.classList.remove('is-open');
          var otherBtn = el.closest('.nro-select').querySelector('.nro-select__btn');
          if (otherBtn) {
            otherBtn.classList.remove('is-open');
            otherBtn.setAttribute('aria-expanded', 'false');
          }
        }
      });
      list.classList.add('is-open');
      btn.classList.add('is-open');
      btn.setAttribute('aria-expanded', 'true');
    }

    function closeDropdown() {
      list.classList.remove('is-open');
      btn.classList.remove('is-open');
      btn.setAttribute('aria-expanded', 'false');
    }

    // ── Event: toggle on button click ───────────────────────────────────────

    btn.addEventListener('click', function (e) {
      e.stopPropagation();
      if (list.classList.contains('is-open')) {
        closeDropdown();
      } else {
        openDropdown();
      }
    });

    // ── Event: select an option ─────────────────────────────────────────────

    list.addEventListener('click', function (e) {
      var item = e.target.closest('.nro-select__item');
      if (!item) return;

      var value = item.dataset.value;
      nativeSelect.value = value;
      labelSpan.textContent = item.textContent.trim();

      list.querySelectorAll('.nro-select__item').forEach(function (li) {
        li.setAttribute('aria-selected', li === item ? 'true' : 'false');
      });

      closeDropdown();
      btn.focus();

      // Notify Shopify FacetFiltersForm (listens to `input` on the form)
      nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
      nativeSelect.dispatchEvent(new Event('input', { bubbles: true }));
    });

    // ── Event: close on outside click ───────────────────────────────────────

    document.addEventListener('click', function () {
      closeDropdown();
    });

    wrapper.addEventListener('click', function (e) {
      e.stopPropagation();
    });

    // ── Keyboard support ────────────────────────────────────────────────────

    btn.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (list.classList.contains('is-open')) {
          closeDropdown();
        } else {
          openDropdown();
          var selected = list.querySelector('[aria-selected="true"]') || list.querySelector('.nro-select__item');
          if (selected) selected.focus();
        }
      }
      if (e.key === 'Escape') closeDropdown();
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        openDropdown();
        var items = Array.from(list.querySelectorAll('.nro-select__item'));
        var target = e.key === 'ArrowDown' ? items[0] : items[items.length - 1];
        if (target) target.focus();
      }
    });

    list.addEventListener('keydown', function (e) {
      var items = Array.from(list.querySelectorAll('.nro-select__item'));
      var idx = items.indexOf(document.activeElement);
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (idx < items.length - 1) items[idx + 1].focus();
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (idx > 0) items[idx - 1].focus(); else { closeDropdown(); btn.focus(); }
      }
      if (e.key === 'Escape') { closeDropdown(); btn.focus(); }
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (items[idx]) items[idx].click();
      }
    });
  }

  // ── Init ──────────────────────────────────────────────────────────────────

  function initAll() {
    document.querySelectorAll(SELECTOR).forEach(buildCustomSelect);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init after Shopify AJAX section re-renders (FacetFiltersForm replaces DOM)
  var observer = new MutationObserver(function (mutations) {
    var needed = false;
    mutations.forEach(function (m) {
      if (m.addedNodes.length) needed = true;
    });
    if (needed) initAll();
  });

  document.addEventListener('DOMContentLoaded', function () {
    var target = document.getElementById('FacetsWrapperDesktop') || document.body;
    observer.observe(target, { childList: true, subtree: true });
    // Also observe body for the full facets container replacement
    observer.observe(document.body, { childList: true, subtree: false });
  });

})();
